import { useEffect } from "react";
import { X, Printer } from "lucide-react";
import { tokenSize, seatLabelPositions } from "@/lib/tableGeometry";

function naturalSort(a, b) {
  return a.localeCompare(b, "it", { numeric: true, sensitivity: "base" });
}

// The browser's own print header/footer (title + URL + date, if the person
// hasn't unchecked "Headers and footers" in their print dialog) always shows
// whatever `document.title` currently is -- normally the generic app default
// ("Il Nostro Matrimonio"). While a print view is open, this swaps it for
// something specific so that header reads something useful instead, and
// restores the previous title on close.
function usePrintTitle(title) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    return () => { document.title = prev; };
  }, [title]);
}

function PrintToolbar({ title, onClose }) {
  return (
    <div className="no-print fixed inset-x-0 top-0 z-50 bg-white border-b flex items-center justify-between px-6 py-3" style={{ borderColor: "#e3ddd2" }}>
      <p className="font-wed text-lg">{title}</p>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => window.print()} className="btn-wed !py-2 !px-4 text-sm flex items-center gap-2" data-testid="print-confirm-button">
          <Printer size={15} /> Stampa
        </button>
        <button type="button" onClick={onClose} className="btn-outline-wed !py-2 !px-4 text-sm flex items-center gap-2" data-testid="print-close-button">
          <X size={15} /> Chiudi
        </button>
      </div>
    </div>
  );
}

// Printable guest-by-table list ("tableau de mariage"): who sits where, plus
// anyone who should have a seat but hasn't been assigned one yet.
export function TableauPrint({ wedding, roomsWithTables, unassignedGuests, onClose }) {
  usePrintTitle(`${wedding.nome_sposi} — Tableau de mariage`);
  const allTables = roomsWithTables.flatMap(({ room, tables }) => tables.map((t) => ({ ...t, room })));
  const multiRoom = roomsWithTables.length > 1;
  const eventDate = new Date(wedding.data_evento).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
  return (
    <div className="print-root bg-white min-h-screen">
      <PrintToolbar title="Tableau de mariage" onClose={onClose} />
      <div className="max-w-4xl mx-auto px-8 pt-24 pb-10 print:pt-2 print:pb-2">
        <div className="text-center mb-6 print:mb-4">
          <p className="text-[10px] uppercase tracking-[0.2em] mb-1" style={{ color: "var(--wed-primary)" }}>Tableau de mariage</p>
          <h1 className="font-wed text-3xl mb-1">{wedding.nome_sposi}</h1>
          <p className="text-neutral-400 text-xs">{eventDate}</p>
          <div className="w-10 h-px mx-auto mt-3" style={{ background: "var(--wed-primary)" }} />
        </div>
        <div className="grid sm:grid-cols-3 gap-x-6 gap-y-5" style={{ columnGap: "1.75rem" }}>
          {[...allTables].sort((a, b) => (a.ordine || 0) - (b.ordine || 0) || naturalSort(a.label, b.label)).map((t) => {
            const seated = (t.seats || []).filter((s) => s && s.nome);
            return (
              <table key={t.id} className="w-full text-xs" style={{ breakInside: "avoid" }}>
                <thead>
                  <tr>
                    <th colSpan={2} className="text-left font-medium text-sm pb-1.5 border-b-2" style={{ borderColor: "var(--wed-primary)" }}>
                      <span
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] mr-1.5 font-wed"
                        style={{ background: "var(--wed-primary)" }}
                      >
                        {t.ordine ?? 0}
                      </span>
                      {t.label}{multiRoom ? ` — ${t.room.nome}` : ""}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {seated.length === 0 ? (
                    <tr><td colSpan={2} className="text-neutral-400 py-1.5 italic">Nessun posto assegnato</td></tr>
                  ) : (
                    seated.map((s, i) => (
                      <tr key={i} className="border-b" style={{ borderColor: "#f0ece1" }}>
                        <td className="py-1 pr-1.5 text-neutral-400 w-4">{i + 1}</td>
                        <td className="py-1">{s.nome}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            );
          })}
        </div>
        {unassignedGuests.length > 0 && (
          <div className="mt-6" style={{ breakInside: "avoid" }}>
            <h2 className="text-sm font-medium mb-1.5 border-b-2 pb-1" style={{ borderColor: "var(--wed-primary)" }}>Senza posto assegnato</h2>
            <ul className="text-xs text-neutral-600 grid sm:grid-cols-3 gap-x-6 gap-y-0.5">
              {unassignedGuests.map((g) => <li key={g.id}>{g.nome}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// Printable budget summary: totals, every line item grouped by category, and the
// per-person cost breakdown -- handy to bring to a vendor meeting or share with
// family helping to pay.
export function BudgetPrint({ wedding, totals, grouped, perPersonSummary, statiLabels, fmtMoney, onClose }) {
  usePrintTitle(`${wedding.nome_sposi} — Budget`);
  return (
    <div className="print-root bg-white min-h-screen">
      <PrintToolbar title="Budget & Organizzazione" onClose={onClose} />
      <div className="max-w-3xl mx-auto px-8 pt-24 pb-16 print:pt-8">
        <div className="text-center mb-8">
          <p className="text-[10px] uppercase tracking-[0.2em] mb-1" style={{ color: "var(--wed-primary)" }}>Budget & Organizzazione</p>
          <h1 className="font-wed text-3xl mb-2">{wedding.nome_sposi}</h1>
          <div className="w-10 h-px mx-auto" style={{ background: "var(--wed-primary)" }} />
        </div>

        <div className="grid grid-cols-4 gap-3 mb-10 text-center rounded-2xl py-5 border" style={{ breakInside: "avoid", background: "#faf6ee", borderColor: "#e3ddd2" }}>
          <div>
            <p className="text-xs text-neutral-400">Budget totale</p>
            <p className="font-wed text-lg" style={{ color: "var(--wed-primary)" }}>{totals.budgetTotale != null ? fmtMoney(totals.budgetTotale) : "—"}</p>
          </div>
          <div>
            <p className="text-xs text-neutral-400">Previsto</p>
            <p className="font-wed text-lg">{fmtMoney(totals.previsto)}</p>
          </div>
          <div>
            <p className="text-xs text-neutral-400">Pagato</p>
            <p className="font-wed text-lg">{fmtMoney(totals.pagato)}</p>
          </div>
          <div>
            <p className="text-xs text-neutral-400">Rimanente</p>
            <p className="font-wed text-lg">{totals.rimanente != null ? fmtMoney(totals.rimanente) : "—"}</p>
          </div>
        </div>

        {perPersonSummary.length > 0 && (
          <div className="mb-10" style={{ breakInside: "avoid" }}>
            <h2 className="font-wed text-xl mb-3 border-b-2 pb-1" style={{ borderColor: "var(--wed-primary)" }}>Costi a persona</h2>
            {perPersonSummary.map((g) => (
              <div key={g.ref} className="flex items-center justify-between text-sm py-1.5 border-b" style={{ borderColor: "#f5f3ec" }}>
                <span>{g.label} ({g.headcount} persone)</span>
                <span className="font-medium">{fmtMoney(g.subtotal)}</span>
              </div>
            ))}
          </div>
        )}

        {grouped.map(({ categoria, rows }) => (
          <div key={categoria} className="mb-6" style={{ breakInside: "avoid" }}>
            <h2 className="font-wed text-lg mb-2 border-b-2 pb-1" style={{ borderColor: "var(--wed-primary)" }}>{categoria}</h2>
            <table className="w-full text-xs">
              <tbody>
                {rows.map((item) => (
                  <tr key={item.id} className="border-b" style={{ borderColor: "#f5f3ec" }}>
                    <td className="py-1.5 pr-2">{item.fatto ? "✓ " : ""}{item.nome}{item.fornitore ? ` — ${item.fornitore}` : ""}</td>
                    <td className="py-1.5 pr-2 text-neutral-500 whitespace-nowrap">{statiLabels[item.stato] || item.stato}</td>
                    <td className="py-1.5 text-right whitespace-nowrap">{fmtMoney(item.costo_effettivo)} / {fmtMoney(item.costo_previsto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

// Printable floor plan: the same geometry the designer uses, rendered statically
// per room with every seat's guest name visible (this is for the couple's own
// use, unlike the public "my table" map which hides other guests' names).
export function FloorPlanPrint({ wedding, roomsWithTables, onClose }) {
  usePrintTitle(`${wedding.nome_sposi} — Disposizione tavoli`);
  return (
    <div className="print-root bg-white min-h-screen">
      <PrintToolbar title="Disposizione tavoli" onClose={onClose} />
      <div className="max-w-5xl mx-auto px-8 pt-24 pb-16 print:pt-8">
        <div className="text-center mb-10">
          <p className="text-[10px] uppercase tracking-[0.2em] mb-1" style={{ color: "var(--wed-primary)" }}>Disposizione tavoli</p>
          <h1 className="font-wed text-3xl mb-2">{wedding.nome_sposi}</h1>
          <div className="w-10 h-px mx-auto" style={{ background: "var(--wed-primary)" }} />
        </div>
        {roomsWithTables.filter((r) => r.tables.length).map(({ room, tables }, i) => {
          const boxes = tables.map((t) => ({ ...t, size: tokenSize(t.shape) }));
          const minX = Math.min(...boxes.map((b) => b.x)) - 70;
          const minY = Math.min(...boxes.map((b) => b.y)) - 70;
          const maxX = Math.max(...boxes.map((b) => b.x + b.size.w)) + 70;
          const maxY = Math.max(...boxes.map((b) => b.y + b.size.h)) + 70;
          return (
            // No page break before the first room -- it would otherwise leave
            // an almost-blank page with just the header on it.
            <div key={room.id} className="mb-12" style={i > 0 ? { breakBefore: "page" } : undefined}>
              <h2 className="font-wed text-xl mb-4 text-center">{room.nome}</h2>
              <svg viewBox={`${minX} ${minY} ${maxX - minX} ${maxY - minY}`} style={{ width: "100%", height: "auto" }}>
                {boxes.map((t) => {
                  const cx = t.x + t.size.w / 2;
                  const cy = t.y + t.size.h / 2;
                  const seatPositions = seatLabelPositions(t.size, t.capacity, t.rotation || 0, !!t.seats_ccw);
                  return (
                    <g key={t.id}>
                      {t.shape === "round" ? (
                        <circle cx={cx} cy={cy} r={t.size.w / 2} fill="white" stroke="#1a1a1a" strokeWidth={1.25} />
                      ) : (
                        <rect x={t.x} y={t.y} width={t.size.w} height={t.size.h} rx={12} fill="white" stroke="#1a1a1a" strokeWidth={1.25} />
                      )}
                      <text x={cx} y={cy - 6} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--wed-primary)">{t.ordine ?? 0}</text>
                      <text x={cx} y={cy + 12} textAnchor="middle" fontSize={15} fontWeight={600} fill="#1a1a1a">{t.label}</text>
                      {seatPositions.map((pos, i) => {
                        const seat = (t.seats || [])[i];
                        const sx = t.x + pos.left;
                        const sy = t.y + pos.top;
                        return (
                          <g key={i}>
                            <circle cx={sx} cy={sy} r={10} fill={seat?.nome ? "white" : "transparent"} stroke={seat?.nome ? "var(--wed-primary)" : "#ccc"} strokeWidth={1} />
                            <text x={sx} y={sy + 3.5} textAnchor="middle" fontSize={9} fill="#555">{i + 1}</text>
                            {seat?.nome && (
                              <text x={sx} y={sy + 24} textAnchor="middle" fontSize={10} fill="#1a1a1a">{seat.nome}</text>
                            )}
                          </g>
                        );
                      })}
                    </g>
                  );
                })}
              </svg>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// One card's worth of content -- the bordered box with QR + couple name +
// caption + date -- reused at three sizes so the same design works filling a
// full page, half a page, or a quarter of one. Stretches to fill its cell
// (border touching the cell's edges) rather than just sitting centered and
// content-sized -- the content itself (even at "full" size) is comfortably
// shorter than a page, so this never risks overflowing; see the safety
// margin on PAGE_HEIGHT below for why an earlier, tighter version did.
function QrCardFace({ wedding, qr, caption, eventDate, size }) {
  const dims = {
    full: { qr: 80, title: "6.4mm", pad: "12mm", radius: "7mm", eyebrow: "3mm", date: "3.6mm", gap: "4.5mm" },
    half: { qr: 48, title: "5mm", pad: "7mm", radius: "5mm", eyebrow: "2.5mm", date: "3mm", gap: "3mm" },
    quarter: { qr: 28, title: "3.4mm", pad: "4.5mm", radius: "3.5mm", eyebrow: "2mm", date: "2.4mm", gap: "2.2mm" },
  }[size];
  return (
    <div
      className="font-wed flex flex-col items-center justify-center text-center"
      style={{ border: "0.5mm solid var(--wed-primary)", borderRadius: dims.radius, padding: dims.pad, width: "100%", height: "100%", boxSizing: "border-box" }}
    >
      <p style={{ fontSize: dims.eyebrow, letterSpacing: "0.25em", textTransform: "uppercase", color: "var(--wed-primary)", marginBottom: dims.gap, fontFamily: "Inter, sans-serif" }}>
        {wedding.nome_sposi}
      </p>
      {qr && <img src={qr} alt="QR code evento" style={{ width: `${dims.qr}mm`, height: `${dims.qr}mm`, borderRadius: "2mm" }} />}
      <h1 style={{ fontSize: dims.title, margin: `${dims.gap} 0 ${dims.gap}`, lineHeight: 1.25 }}>{caption}</h1>
      <div style={{ width: "8mm", height: "0.3mm", background: "var(--wed-primary)", marginBottom: dims.gap }} />
      <p style={{ fontSize: dims.date, color: "#6b6459", fontFamily: "Inter, sans-serif" }}>
        {eventDate}{wedding.location ? ` · ${wedding.location}` : ""}
      </p>
    </div>
  );
}

const CUT_LINE = "0.3mm dashed #ccc";
const CELL = { boxSizing: "border-box", padding: "4mm" };

// Three pages in one print job, same design at decreasing size, so a single
// "Stampa" + a few scissor cuts produces several ready-to-display cards
// instead of printing one card seven times by hand: page 1 is one card
// filling the page, page 2 is the same card twice (cut once, horizontally),
// page 3 is four of them in a 2x2 grid (cut twice). Dashed guide lines mark
// where to cut.
//
// Each page section is deliberately given less height (260mm) than the full
// printable area (277mm = A4's 297mm minus the 10mm @page margin on each
// side, set globally in App.css) -- a safety margin against exactly that kind
// of rounding drift, rather than cutting it as close as possible.
export function QrCardPrint({ wedding, qr, caption, onClose }) {
  usePrintTitle(`${wedding.nome_sposi} — QR code`);
  const eventDate = new Date(wedding.data_evento).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
  const PAGE_HEIGHT = "260mm";
  const face = (size) => <QrCardFace wedding={wedding} qr={qr} caption={caption} eventDate={eventDate} size={size} />;

  return (
    <div className="print-root bg-white">
      <PrintToolbar title="QR code da stampare" onClose={onClose} />
      <div className="no-print h-20" />

      <div style={{ ...CELL, height: PAGE_HEIGHT, breakAfter: "page", pageBreakAfter: "always" }}>{face("full")}</div>

      <div style={{ height: PAGE_HEIGHT, breakAfter: "page", pageBreakAfter: "always" }} className="flex flex-col">
        <div style={{ ...CELL, height: "50%", borderBottom: CUT_LINE }}>{face("half")}</div>
        <div style={{ ...CELL, height: "50%" }}>{face("half")}</div>
      </div>

      <div style={{ height: PAGE_HEIGHT }} className="grid grid-cols-2 grid-rows-2">
        <div style={{ ...CELL, borderRight: CUT_LINE, borderBottom: CUT_LINE }}>{face("quarter")}</div>
        <div style={{ ...CELL, borderBottom: CUT_LINE }}>{face("quarter")}</div>
        <div style={{ ...CELL, borderRight: CUT_LINE }}>{face("quarter")}</div>
        <div style={CELL}>{face("quarter")}</div>
      </div>
    </div>
  );
}
